import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';

import {
  pennylaneCustomerInvoiceStatusLabels,
  pennylanePaymentStatusLabels,
} from './utils/pennylane-enum-labels.util';
import type { Invoice } from './hooks/useMissionPennylaneAccountingQuery.hook';
import { usePennylaneAmountFormatter } from './hooks/usePennylaneAmountFormatter.hook';
import { formatDate } from './utils/format-date.util';

export const PennylaneInvoiceAccordion = ({
  invoice,
}: Readonly<{
  invoice: Invoice;
}>) => {
  const formatPennylaneAmount = usePennylaneAmountFormatter();

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="action" fontSize="small" />
          <Typography variant="body2">
            Facture{' '}
            {invoice.invoice_number
              ? `N° ${invoice.invoice_number}`
              : `(ID ${String(invoice.id)})`}
          </Typography>
          {!!invoice.status && (
            <Chip
              label={
                pennylaneCustomerInvoiceStatusLabels[invoice.status] ??
                invoice.status
              }
              size="small"
              variant="outlined"
            />
          )}
          {!!invoice.paid && (
            <Chip color="success" label="Réglée" size="small" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3 }}>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Montant HT
              </Typography>
              <Typography variant="body2">
                {formatPennylaneAmount(
                  invoice.currency_amount_before_tax,
                  invoice.currency,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Montant TTC
              </Typography>
              <Typography variant="body2">
                {formatPennylaneAmount(
                  invoice.currency_amount,
                  invoice.currency,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Reste à payer
              </Typography>
              <Typography variant="body2">
                {formatPennylaneAmount(
                  invoice.remaining_amount_with_tax,
                  invoice.currency,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Date
              </Typography>
              <Typography variant="body2">
                {formatDate(invoice.date)}
              </Typography>
            </Box>
            <Box>
              <Typography color="text.secondary" variant="caption">
                Échéance
              </Typography>
              <Typography variant="body2">
                {formatDate(invoice.deadline)}
              </Typography>
            </Box>
            {!!invoice.external_reference && (
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Référence e-SOCNA
                </Typography>
                <Typography variant="body2">
                  {invoice.external_reference}
                </Typography>
              </Box>
            )}
          </Stack>

          {!!invoice.resolvedPayments?.length && (
            <Box>
              <Typography gutterBottom variant="caption">
                <strong>Paiements ({invoice.resolvedPayments.length})</strong>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Libellé</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.resolvedPayments.map((payment) => (
                    <TableRow key={String(payment.id)}>
                      <TableCell>{payment.label}</TableCell>
                      <TableCell>
                        {formatPennylaneAmount(
                          payment.currency_amount,
                          payment.currency,
                        )}
                      </TableCell>
                      <TableCell>
                        {pennylanePaymentStatusLabels[payment.status] ??
                          payment.status}
                      </TableCell>
                      <TableCell>{formatDate(payment.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {!!invoice.resolvedMatchedTransactions?.length && (
            <Box>
              <Typography gutterBottom variant="caption">
                <strong>
                  Transactions rapprochées (
                  {invoice.resolvedMatchedTransactions.length})
                </strong>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Libellé</TableCell>
                    <TableCell>Solde restant</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.resolvedMatchedTransactions.map((tx) => (
                    <TableRow key={String(tx.id)}>
                      <TableCell>{tx.label}</TableCell>
                      <TableCell>
                        {formatPennylaneAmount(
                          tx.outstanding_balance,
                          tx.currency,
                        )}
                      </TableCell>
                      <TableCell>{formatDate(tx.date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
